import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { FilterSection } from "../../../components/patients/FilterSection";
import { PatientListTable } from "../../../components/patients/PatientListTable";
import { FilterModal, AppliedFilters } from "../../../components/patients/FilterModal";
import { CreateGroupModal } from "../../../components/patients/CreateGroupModal";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Loader2 } from "lucide-react";
import { usePatientDetailsForGroups } from "@/hooks/patient/usePatientDetailsForGroups";
import { serviceFactory, graphqlClient } from "@/services/core/serviceFactory";
import { useAuth } from "@/contexts/AuthContext";
import { API_CONFIG } from "@/config/envConfig";

interface DisplayPatient {
  id: string;
  patientName: string;
  emr: string;
  gender: string;
  dateOfBirth: string;
  age: number;
  phoneNumber: string;
  providerDetails: string;
  clinicDetails: string;
  primaryDx: string;
  secondaryDx: string;
  insuranceType: string;
}

const CreateGroupByFilters = () => {
  const [nameFilter, setNameFilter] = useState("");
  const [phoneFilter, setPhoneFilter] = useState("");
  const [emrFilter, setEmrFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage] = useState(50); // 50 records per page
  const [displayPatients, setDisplayPatients] = useState<DisplayPatient[]>([]);
  const [selectedPatients, setSelectedPatients] = useState<string[]>([]);
  const [selectCount, setSelectCount] = useState<number>(0);
  const [countBasedPatientIds, setCountBasedPatientIds] = useState<string[]>([]);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false);
  const [isFetchingCountPatients, setIsFetchingCountPatients] = useState(false);
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({});
  const navigate = useNavigate();
  const { toast } = useToast();
  const { hasuraToken, token, user } = useAuth();

  const offset = (currentPage - 1) * entriesPerPage;
  const { patients: apiPatients, isLoading, error, totalCount, refetch } = usePatientDetailsForGroups(entriesPerPage, offset);

  useEffect(() => {
    document.title = "Create New Group By Filters | Admin";
  }, []);

  // Transform API data to display format
  useEffect(() => {
    console.log('API Patients received:', apiPatients);
    
    if (apiPatients && apiPatients.length > 0) {
      const transformed = apiPatients.map(patient => {
        const age = patient.dob 
          ? new Date().getFullYear() - new Date(patient.dob).getFullYear()
          : 0;
        
        // Format gender
        const genderDisplay = patient.gender === 'M' ? 'Male' : patient.gender === 'F' ? 'Female' : patient.gender || '';
        
        return {
          id: patient.enrollment_patient_id?.toString() || patient.emr_patient_clinic_data_id?.toString() || '',
          patientName: `${patient.first_name || ''} ${patient.last_name || ''}`.trim(),
          emr: patient.emr_id || '',
          gender: genderDisplay,
          dateOfBirth: patient.dob || '',
          age,
          phoneNumber: patient.mobile_number || patient.home_number || patient.work_phone || '',
          providerDetails: patient.provider_name?.trim() || '-',
          clinicDetails: patient.clinic_name || '',
          primaryDx: '-',
          secondaryDx: '-',
          insuranceType: '-'
        };
      });
      
      console.log('Transformed patients:', transformed);
      setDisplayPatients(transformed);
    } else if (apiPatients && apiPatients.length === 0) {
      console.log('No patients found');
      setDisplayPatients([]);
    }
  }, [apiPatients]);

  const handleSearch = () => {
    const conditions: any[] = [];
    
    if (nameFilter.trim()) {
      conditions.push({
        _or: [
          { first_name: { _ilike: `%${nameFilter}%` } },
          { last_name: { _ilike: `%${nameFilter}%` } }
        ]
      });
    }
    
    if (phoneFilter.trim()) {
      conditions.push({
        _or: [
          { mobile_number: { _ilike: `%${phoneFilter}%` } },
          { home_number: { _ilike: `%${phoneFilter}%` } },
          { work_phone: { _ilike: `%${phoneFilter}%` } }
        ]
      });
    }
    
    if (emrFilter.trim()) {
      conditions.push({ emr_id: { _ilike: `%${emrFilter}%` } });
    }
    
    const searchFilters = conditions.length > 0 ? { _and: conditions } : {};
    
    setCurrentPage(1);
    setSelectedPatients([]);
    refetch(searchFilters);
    
    toast({
      title: "Search Applied",
      description: "Searching for patients matching your criteria.",
    });
  };

  const handleFilter = () => {
    setIsFilterModalOpen(true);
  };

  // Fetch patients based on select count - includes applied filters
  const fetchCountBasedPatients = async (count: number) => {
    if (count <= 0) {
      setCountBasedPatientIds([]);
      return;
    }

    setIsFetchingCountPatients(true);
    try {
      if (!hasuraToken) {
        throw new Error('Authentication required');
      }

      // Build where condition including applied filters
      const whereCondition: any = {
        patient_group_id: { _is_null: true }
      };

      // Include applied filters in the where condition
      if (appliedFilters.clinic_id && appliedFilters.clinic_id.length > 0) {
        whereCondition.clinic_id = { _in: appliedFilters.clinic_id };
      }
      
      if (appliedFilters.provider_id && appliedFilters.provider_id.length > 0) {
        whereCondition.provider_id = { _in: appliedFilters.provider_id };
      }
      
      if (appliedFilters.clinic_facility_id && appliedFilters.clinic_facility_id.length > 0) {
        whereCondition.clinic_facility_id = { _in: appliedFilters.clinic_facility_id };
      }
      
      if (appliedFilters.primary_insurance_payor && appliedFilters.primary_insurance_payor.length > 0) {
        whereCondition.primary_insurance_payor = { _in: appliedFilters.primary_insurance_payor };
      }
      
      if (appliedFilters.secondary_insurance_payor && appliedFilters.secondary_insurance_payor.length > 0) {
        whereCondition.secondary_insurance_payor = { _in: appliedFilters.secondary_insurance_payor };
      }
      
      if (appliedFilters.condition && appliedFilters.condition.length > 0) {
        whereCondition.condition = { _in: appliedFilters.condition };
      }
      
      if (appliedFilters.zip_code && appliedFilters.zip_code.length > 0) {
        whereCondition.zip_code = { _in: appliedFilters.zip_code };
      }
      
      if (appliedFilters.program_eligibility && appliedFilters.program_eligibility.length > 0) {
        whereCondition.program_eligibility = { _in: appliedFilters.program_eligibility };
      }

      if (selectedPatients.length > 0) {
        whereCondition.enrollment_patient_id = { _nin: selectedPatients.map(id => parseInt(id)) };
      }

      console.log('[fetchCountBasedPatients] Applied filters:', appliedFilters);
      console.log('[fetchCountBasedPatients] Where condition:', whereCondition);

      const query = `
        query FetchCountBasedPatients($limit: Int!, $where: pep_vw_patient_details_bool_exp) {
          pep_vw_patient_details(limit: $limit, where: $where) {
            enrollment_patient_id
          }
        }
      `;

      const response = await graphqlClient.request(
        {
          query,
          variables: {
            limit: count,
            where: whereCondition
          }
        },
        hasuraToken
      );

      const patientIds = (response as any)?.data?.pep_vw_patient_details?.map(
        (p: any) => p.enrollment_patient_id?.toString()
      ).filter(Boolean) || [];

      setCountBasedPatientIds(patientIds);
      
      toast({
        title: "Patients Selected",
        description: `Selected ${patientIds.length} patients from filtered results.`,
      });
    } catch (error) {
      console.error('Error fetching count-based patients:', error);
      toast({
        title: "Error",
        description: "Failed to fetch patients by count.",
        variant: "destructive",
      });
    } finally {
      setIsFetchingCountPatients(false);
    }
  };

  // Handle select count change
  const handleSelectCountChange = async (count: number) => {
    setSelectCount(count);
    if (count > 0) {
      await fetchCountBasedPatients(count);
    } else {
      setCountBasedPatientIds([]);
    }
  };

  const handlePatientSelect = (patientId: string, checked: boolean) => {
    if (checked) {
      setSelectedPatients(prev => [...prev, patientId]);
    } else {
      setSelectedPatients(prev => prev.filter(id => id !== patientId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    const currentPagePatientIds = displayPatients.map(patient => patient.id);
    
    if (checked) {
      // Add current page patients to existing selections
      setSelectedPatients(prev => [...new Set([...prev, ...currentPagePatientIds])]);
    } else {
      // Remove current page patients from selections
      setSelectedPatients(prev => prev.filter(id => !currentPagePatientIds.includes(id)));
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleCreateGroup = () => {
    const totalSelectedCount = selectedPatients.length + countBasedPatientIds.length;
    
    if (totalSelectedCount === 0) {
      toast({
        title: "No Patients Selected",
        description: "Please select patients or enter a count to create a group.",
        variant: "destructive",
      });
      return;
    }

    setIsCreateGroupModalOpen(true);
  };

  const handleSaveGroup = async (groupData: { name: string; note: string }) => {
    setIsCreatingGroup(true);
    try {
      if (!user?.id || !token) {
        throw new Error('Authentication required');
      }

      // Combine manually selected and count-based patient IDs
      const allPatientIds = [...new Set([...selectedPatients, ...countBasedPatientIds])];
      
      if (allPatientIds.length === 0) {
        toast({
          title: "No Patients Selected",
          description: "Please select patients to create a group.",
          variant: "destructive",
        });
        setIsCreatingGroup(false);
        return;
      }

      // Build the payload matching the API structure
      const payload = {
        groupName: groupData.name,
        groupFilters: JSON.stringify({
          clinic_id: 74 // You may need to make this dynamic based on selected clinic
        }),
        isArchived: false,
        patientIds: allPatientIds.map(id => parseInt(id))
      };

      const endpoint = `${API_CONFIG.API_BASE}/api/ngpep/${user.id}/patient-group`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      toast({
        title: "Group Created Successfully",
        description: `Created "${groupData.name}" with ${allPatientIds.length} patients.`,
      });
      navigate("/admin/patient-groups");
    } catch (error) {
      console.error('Error creating group:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "An error occurred while creating the group.",
        variant: "destructive",
      });
    } finally {
      setIsCreatingGroup(false);
    }
  };

  const handleGoBack = () => {
    navigate("/admin/patient-groups");
  };

  const handleApplyFilters = (filters: AppliedFilters) => {
    setAppliedFilters(filters);
    
    // Build where clause with _in operators (don't include patient_group_id as it's handled by the hook)
    const whereCondition: any = {};
    
    if (filters.clinic_id && filters.clinic_id.length > 0) {
      whereCondition.clinic_id = { _in: filters.clinic_id };
    }
    
    if (filters.provider_id && filters.provider_id.length > 0) {
      whereCondition.provider_id = { _in: filters.provider_id };
    }
    
    if (filters.clinic_facility_id && filters.clinic_facility_id.length > 0) {
      whereCondition.clinic_facility_id = { _in: filters.clinic_facility_id };
    }
    
    if (filters.primary_insurance_payor && filters.primary_insurance_payor.length > 0) {
      whereCondition.primary_insurance_payor = { _in: filters.primary_insurance_payor };
    }
    
    if (filters.secondary_insurance_payor && filters.secondary_insurance_payor.length > 0) {
      whereCondition.secondary_insurance_payor = { _in: filters.secondary_insurance_payor };
    }
    
    if (filters.condition && filters.condition.length > 0) {
      whereCondition.condition = { _in: filters.condition };
    }
    
    if (filters.zip_code && filters.zip_code.length > 0) {
      whereCondition.zip_code = { _in: filters.zip_code };
    }
    
    if (filters.program_eligibility && filters.program_eligibility.length > 0) {
      whereCondition.program_eligibility = { _in: filters.program_eligibility };
    }
    
    setCurrentPage(1);
    setSelectedPatients([]);
    refetch(whereCondition);
    
    toast({
      title: "Filters Applied",
      description: "Advanced filters have been applied to the patient list.",
    });
  };

  return (
    <div className="space-y-6">
      <header className="">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleGoBack}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Patient Group List
          </Button>
          <span className="text-muted-foreground">&gt;&gt;</span>
          <span className="text-muted-foreground">Create New Group</span>
        </div>
        
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Create New Group By Filters</h1>
          </div>
          <Button onClick={handleCreateGroup} className="bg-primary hover:bg-primary/90 px-6">
            Create Group
          </Button>
        </div>
      </header>

      <main className="space-y-6">
        <FilterSection
          nameValue={nameFilter}
          phoneValue={phoneFilter}
          emrValue={emrFilter}
          onNameChange={setNameFilter}
          onPhoneChange={setPhoneFilter}
          onEmrChange={setEmrFilter}
          onSearch={handleSearch}
          onFilter={handleFilter}
        />

        <div className="relative">
          {(isLoading || isFetchingCountPatients) && (
            <div className="absolute inset-0 bg-background/50 backdrop-blur-sm z-10 flex justify-center items-center rounded-lg">
              <div className="flex items-center gap-2 bg-background border rounded-lg px-4 py-2 shadow-lg">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                <span className="text-sm font-medium text-foreground">
                  {isFetchingCountPatients ? "Fetching patients..." : "Loading..."}
                </span>
              </div>
            </div>
          )}
          
          {error ? (
            <div className="text-center py-8 text-destructive">
              <p>Error loading patients: {error}</p>
            </div>
          ) : (
            <PatientListTable
              patients={displayPatients}
              selectedPatients={selectedPatients}
              onPatientSelect={handlePatientSelect}
              onSelectAll={handleSelectAll}
              selectCount={selectCount}
              onSelectCountChange={handleSelectCountChange}
              filteredCount={totalCount}
              currentPage={currentPage}
              totalPages={Math.ceil(totalCount / entriesPerPage)}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </main>

      <FilterModal
        open={isFilterModalOpen}
        onOpenChange={setIsFilterModalOpen}
        onApply={handleApplyFilters}
      />

      <CreateGroupModal
        open={isCreateGroupModalOpen}
        onOpenChange={setIsCreateGroupModalOpen}
        onSave={handleSaveGroup}
        selectedPatientsCount={selectedPatients.length + countBasedPatientIds.length}
        isLoading={isCreatingGroup}
      />
    </div>
  );
};

export default CreateGroupByFilters;